import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { publicApi, currency } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/lib/content";
import { Link } from "react-router-dom";
import { CalendarCheck } from "lucide-react";

export const STATUS_STYLES = {
  pending: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  confirmed: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  in_progress: "bg-violet-500/15 text-violet-300 border-violet-500/40",
  completed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  cancelled: "bg-red-500/15 text-red-300 border-red-500/40",
};

export function StatusBadge({ status }) {
  return (
    <span className={`inline-block px-3 py-1 rounded-full text-xs font-mono uppercase tracking-wider border ${STATUS_STYLES[status] || STATUS_STYLES.pending}`}>
      {(status || "pending").replace("_", " ")}
    </span>
  );
}

export default function Reservations() {
  const { user, loading, login } = useAuth();
  const { t } = useContent();
  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["my-orders"],
    queryFn: async () => (await publicApi.get("/orders/mine")).data,
    enabled: !!user,
  });

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-24 text-muted2">Loading…</div>;

  const payBalance = async (orderId) => {
    try {
      const { data } = await publicApi.post("/payments/checkout", {
        order_id: orderId, payment_type: "balance", origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start balance payment.");
    }
  };

  const payDeposit = async (orderId) => {
    try {
      const { data } = await publicApi.post("/payments/checkout", {
        order_id: orderId, payment_type: "deposit", origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start deposit payment.");
    }
  };

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <CalendarCheck className="w-12 h-12 text-gold/50 mx-auto mb-6" />
        <h1 className="font-serif text-3xl text-cream">{t("reservations.signin_title")}</h1>
        <p className="text-muted2 mt-3">{t("reservations.signin_subtitle")}</p>
        <button onClick={() => { localStorage.setItem("post_login_redirect", "/reservations"); login(); }} className="btn-gold mt-8">Sign in</button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h1 className="font-serif text-4xl text-cream mb-10">{t("reservations.title")}</h1>
      {isLoading ? (
        <p className="text-muted2">Loading…</p>
      ) : orders.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-muted2">{t("reservations.empty")}</p>
          <Link to="/catalog" className="btn-gold mt-6">{t("reservations.empty_cta")}</Link>
        </div>
      ) : (
        <div className="space-y-5">
          {orders.map((o) => (
            <div key={o.id} data-testid={`reservation-${o.id}`} className="gold-card !border-gold/25 p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <p className="font-mono text-xs text-muted2">#{o.id.slice(-8)}</p>
                  <p className="text-cream mt-1 text-sm">{o.event_start} → {o.event_end}</p>
                </div>
                <StatusBadge status={o.status} />
              </div>
              <div className="border-t border-gold/15 pt-4 space-y-2">
                {o.items.map((i, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span className="text-cream/80">{i.name} × {i.quantity} · {i.days}d</span>
                    <span className="text-muted2">{currency(i.price_per_day * i.quantity * i.days)}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center mt-4 pt-4 border-t border-gold/15">
                <span className="text-muted2 text-sm">{o.delivery_address}</span>
                <span className="text-gold font-serif text-xl">{currency(o.total)}</span>
              </div>
              <div className="mt-4 pt-4 border-t border-gold/15 grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center justify-between bg-ink-800 rounded-lg px-3 py-2">
                  <span className="text-muted2">Deposit (50%) · {currency(o.deposit_amount)}</span>
                  <span className={o.deposit_status === "paid" ? "text-emerald-400 font-medium" : "text-amber-300"}>{o.deposit_status === "paid" ? "Paid" : "Due"}</span>
                </div>
                <div className="flex items-center justify-between bg-ink-800 rounded-lg px-3 py-2">
                  <span className="text-muted2">Balance (50%) · {currency(o.balance_amount)}</span>
                  <span className={o.balance_status === "paid" ? "text-emerald-400 font-medium" : "text-amber-300"}>{o.balance_status === "paid" ? "Paid" : "Due"}</span>
                </div>
              </div>
              {o.status !== "cancelled" && (
                <div className="mt-4 flex flex-wrap gap-3">
                  {o.deposit_status !== "paid" && (
                    <button data-testid={`pay-deposit-${o.id}`} onClick={() => payDeposit(o.id)} className="btn-gold !py-2.5 !px-6">Pay 50% Deposit</button>
                  )}
                  {o.deposit_status === "paid" && o.balance_status !== "paid" && (
                    <button data-testid={`pay-balance-${o.id}`} onClick={() => payBalance(o.id)} className="btn-gold !py-2.5 !px-6">Pay Balance</button>
                  )}
                  {o.payment_status === "paid" && (
                    <span className="text-emerald-400 text-sm inline-flex items-center">Fully paid — thank you!</span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
